import { Injectable, OnDestroy } from '@angular/core';
import { addSeconds, startOfSecond } from 'date-fns';
import { Seq } from 'immutable';
import { isEqual } from 'lodash-es';
import { combineLatest, of, ReplaySubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  takeUntil,
  withLatestFrom,
} from 'rxjs/operators';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { TickService } from 'src/app/shared/tick.service';
import { ViewModelService } from 'src/app/shared/view-model.service';
import {
  ActiveAlertContextType,
  EntityType,
  VisibilityType,
} from 'src/graphql/generated/graphql.generated';
import { UserService } from 'src/lib/tcs-identity/user.service';
import { selectAllUnacknowledgedActiveAlertsByCompanyId } from 'src/store/entityStores/active-alert-selectors';
import { ActiveAlertRecord } from 'src/store/generated/active-alert-record.generated';
import { undefinedOrMap } from 'src/util/rxjs/operators';
import { filterNull } from 'src/util/rxjs/operators/filter-null';

const visibilityLevels = [
  VisibilityType.Author,
  VisibilityType.Region,
  VisibilityType.CommunicationsCenter,
];
type ActiveAlertBasic = Pick<
  ActiveAlertRecord,
  | 'activationTime'
  | 'visibilityType'
  | 'entityType'
  | 'entityId'
  | 'authorId'
  | 'regionId'
  | 'communicationCenterId'
  | 'escalationDurationInSeconds'
>;
function getEscalationTimeOrNull({
  alert,
  between,
}: {
  alert: ActiveAlertBasic;
  between: { start: VisibilityType; end: VisibilityType };
}): Date | null {
  const distance = visibilityLevels.indexOf(between.end) - visibilityLevels.indexOf(between.start);
  const seconds = alert.escalationDurationInSeconds ?? -1;
  const escalateSeconds = seconds < 0 || distance < 0 ? -1 : seconds * (distance + 1);
  return alert.activationTime != null && escalateSeconds >= 0
    ? startOfSecond(addSeconds(alert.activationTime, escalateSeconds))
    : null;
}
function getVisibilityType(alert: ActiveAlertBasic, currentTime: Date): VisibilityType {
  if (alert.activationTime == null) {
    return alert.visibilityType;
  }
  const escalateToVisibilityType = visibilityLevels
    .slice()
    .reverse()
    .find((visibilityType) => {
      const nextIndex = Math.min(
        visibilityLevels.indexOf(alert.visibilityType) + 1,
        visibilityLevels.length - 1,
      );
      const escalationTime = getEscalationTimeOrNull({
        alert,
        between: {
          start: visibilityLevels[nextIndex],
          end: visibilityType,
        },
      });
      return escalationTime != null && currentTime >= escalationTime;
    });
  return escalateToVisibilityType ?? alert.visibilityType;
}
function canEscalateToAll(alert: ActiveAlertBasic, currentTime: Date): boolean {
  const escalationTime = getEscalationTimeOrNull({
    alert,
    between: {
      start: alert.visibilityType,
      end: visibilityLevels[visibilityLevels.length - 1],
    },
  });
  return (
    escalationTime != null &&
    currentTime >= escalationTime &&
    alert.visibilityType === VisibilityType.CommunicationsCenter
  );
}
function isAlertActive({
  context,
  alert,
  currentTime,
  userId,
  cardId,
  regionId,
  commCenterId,
}: {
  context: ActiveAlertContextType;
  alert: ActiveAlertBasic;
  currentTime: Date;
  userId: string;
  regionId: string;
  commCenterId: string;
  cardId?: string;
}): boolean {
  const visibilityType = getVisibilityType(alert, currentTime);

  const isEntityOnlyAlert =
    visibilityType === VisibilityType.EntityOnly && alert.entityId === cardId;

  const isAuthorAlert =
    (visibilityType === VisibilityType.Author || alert.visibilityType === VisibilityType.Author) &&
    alert.authorId === userId;

  const isRegionAlert = visibilityType === VisibilityType.Region && alert.regionId === regionId;

  const isCommCenterAlert =
    visibilityType === VisibilityType.CommunicationsCenter &&
    alert.communicationCenterId === commCenterId;

  const isActive =
    isEntityOnlyAlert ||
    isAuthorAlert ||
    isRegionAlert ||
    isCommCenterAlert ||
    canEscalateToAll(alert, currentTime);

  const isValidAlertContext =
    (context === ActiveAlertContextType.Region && alert.entityType !== EntityType.Card) ||
    (context === ActiveAlertContextType.Card && alert.entityType === EntityType.Card);

  return isActive && isValidAlertContext;
}

@Injectable({
  providedIn: 'root',
})
export class ActiveAlertObserverService implements OnDestroy {
  private _activeRegionAlerts$ = new ReplaySubject<Seq.Indexed<ActiveAlertRecord>>(1);
  private _activeCardAlerts$ = new ReplaySubject<Seq.Indexed<ActiveAlertRecord>>(1);
  private _allUnacknowledgedAlerts$ = new ReplaySubject<Seq.Indexed<ActiveAlertRecord>>(1);
  private _onDestroy$ = new Subject();

  public get activeRegionAlerts$() {
    return this._activeRegionAlerts$.asObservable();
  }

  public get activeCardAlerts$() {
    return this._activeCardAlerts$.asObservable();
  }

  constructor(
    private _vm: ViewModelService,
    private _user: UserService,
    private _companyContext: CompanyContextService,
    _tick: TickService,
  ) {
    const timer$ = _tick.everyFiveSeconds$.pipe(debounceTime(500));
    const companyId$ = this._companyContext.company$.pipe(
      filterNull(),
      map((company) => company.id),
      distinctUntilChanged(),
    );
    const unacknowledgedActiveAlerts$ = combineLatest([timer$, companyId$]).pipe(
      switchMap(([t, companyId]) =>
        _vm.store.select(selectAllUnacknowledgedActiveAlertsByCompanyId(companyId)),
      ),
      distinctUntilChanged((a, b) =>
        isEqual(a.map((_) => _.id).toArray(), b.map((_) => _.id).toArray()),
      ),
    );
    const cardRegionCommCenter$ = combineLatest([
      _vm.getCurrentCard().pipe(distinctUntilChanged((c1, c2) => c1?.id === c2?.id)),
      _vm.getCurrentRegion().pipe(
        distinctUntilChanged((r1, r2) => r1?.id === r2?.id),
        switchMap((region) =>
          combineLatest([of(region), _vm.getCurrentCommunicationCenter(region)]),
        ),
      ),
    ]).pipe(debounceTime(500));

    unacknowledgedActiveAlerts$
      .pipe(
        switchMap((alerts) =>
          combineLatest([of(alerts), cardRegionCommCenter$, _user.distinctUser$]),
        ),
        map(([alerts, [card, [region, commCenter]], user]) => {
          if (card != null && region == null) {
            region = card.region;
          }
          return region != null && user != null
            ? { alerts, card, region, commCenter, user }
            : undefined;
        }),
        undefinedOrMap(({ alerts, card, region, commCenter, user }) => {
          this._allUnacknowledgedAlerts$.next(alerts);

          const now = startOfSecond(Date.now());
          const getArgs = (ctx: ActiveAlertContextType, alert: ActiveAlertBasic) => ({
            context: ctx,
            alert,
            currentTime: now,
            cardId: card?.id,
            regionId: region.id,
            commCenterId: commCenter?.id ?? region.communicationCenterId,
            userId: user.id,
          });
          return {
            cardContextAlerts: alerts.filter((alert) =>
              isAlertActive(getArgs(ActiveAlertContextType.Card, alert)),
            ),
            regionContextAlerts: alerts.filter((alert) =>
              isAlertActive(getArgs(ActiveAlertContextType.Region, alert)),
            ),
          };
        }),
        filterNull(),
        takeUntil(this._onDestroy$),
      )
      .subscribe(({ cardContextAlerts, regionContextAlerts }) => {
        this._activeRegionAlerts$.next(regionContextAlerts);
        this._activeCardAlerts$.next(cardContextAlerts);
      });
  }

  observeActiveAlertsByCardId(cardId: string) {
    return combineLatest([
      this._allUnacknowledgedAlerts$.pipe(
        map((alerts) => alerts.filter((a) => a.entityId === cardId)),
      ),
      this._vm
        .getCardSummaryById(cardId)
        .pipe(distinctUntilChanged((c1, c2) => c1 == null || c2 == null || c1?.id === c2?.id)),
      this._vm.getCurrentRegion().pipe(
        distinctUntilChanged((r1, r2) => r1 == null || r2 == null || r1?.id === r2?.id),
        switchMap((region) =>
          combineLatest([of(region), this._vm.getCurrentCommunicationCenter(region)]),
        ),
      ),
    ]).pipe(
      withLatestFrom(this._user.user$),
      map(([[alerts, card, [region, commCenter]], user]) => {
        const regionId = region?.id ?? card?.regionId;
        const commCenterId = commCenter?.id ?? region?.id ?? card?.communicationCenterId ?? null;
        return regionId != null && commCenterId != null && user != null
          ? { alerts, regionId, commCenterId, user }
          : undefined;
      }),
      undefinedOrMap(({ alerts, regionId, commCenterId, user }) => {
        const now = startOfSecond(Date.now());
        return alerts.filter((alert) =>
          isAlertActive({
            context: ActiveAlertContextType.Card,
            alert,
            currentTime: now,
            cardId,
            regionId,
            commCenterId,
            userId: user.id,
          }),
        );
      }),
      map((alerts) => (alerts == null ? Seq.Indexed<ActiveAlertRecord>() : alerts)),
    );
  }

  observeActiveAlertsByOosEventIds(oosEventIds: string[]) {
    return combineLatest([
      this._allUnacknowledgedAlerts$.pipe(
        map((alerts) =>
          alerts.filter((a) => a.entityId != null && oosEventIds.includes(a.entityId)),
        ),
      ),
      this._vm.getCurrentRegion().pipe(
        distinctUntilChanged((r1, r2) => r1 == null || r2 == null || r1?.id === r2?.id),
        switchMap((region) =>
          combineLatest([of(region), this._vm.getCurrentCommunicationCenter(region)]),
        ),
      ),
    ]).pipe(
      withLatestFrom(this._user.user$),
      map(([[alerts, [region, commCenter]], user]) => {
        const regionId = region?.id ?? null;
        const commCenterId = commCenter?.id ?? region?.id ?? null;
        return regionId != null && commCenterId != null && user != null
          ? { alerts, regionId, commCenterId, user }
          : undefined;
      }),
      undefinedOrMap(({ alerts, regionId, commCenterId, user }) => {
        const now = startOfSecond(Date.now());
        return alerts.filter((alert) =>
          isAlertActive({
            context: ActiveAlertContextType.Region,
            alert,
            currentTime: now,
            regionId,
            commCenterId,
            userId: user.id,
          }),
        );
      }),
      map((alerts) => (alerts == null ? Seq.Indexed<ActiveAlertRecord>() : alerts)),
    );
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
